import axios from 'axios';
import * as Sentry from '@sentry/react';
import type { KlydoLoopUploadTiming } from '../api/uploads';

export async function uploadFileToSignedUrl(
  signedUrl: string,
  data: string | Blob,
  onProgress?: (pct: number) => void,
): Promise<{ blobMs: number; putMs: number; sizeBytes: number }> {
  const blobStart = Date.now();
  const file =
    typeof data === 'string' ? await (await fetch(data)).blob() : data;
  const blobMs = Date.now() - blobStart;
  const putStart = Date.now();
  await axios.put(signedUrl, file, {
    onUploadProgress: (e) => {
      if (e.total && onProgress)
        onProgress(Math.round((e.loaded * 100) / e.total));
    },
  });
  return { blobMs, putMs: Date.now() - putStart, sizeBytes: file.size };
}

export type KlydoLoopUploadStep =
  | 'signedUrl'
  | 'readFile'
  | 'storageUpload'
  | 'process';

// uploadKlydoLoop fills each timing field only after its step succeeded, so the
// first missing field is the step that threw. Inside uploadFileToSignedUrl the
// PUT is the only axios call, so a non-axios error there comes from turning the
// data URL back into a Blob.
export const getKlydoLoopUploadFailedStep = (
  timing: KlydoLoopUploadTiming,
  error: unknown,
): KlydoLoopUploadStep => {
  if (timing.signedUrlMs === undefined) return 'signedUrl';
  if (timing.putMs === undefined) {
    return axios.isAxiosError(error) ? 'storageUpload' : 'readFile';
  }
  return 'process';
};

// A new Error, not the original: Sentry's ignoreErrors drops
// 'AxiosError: Network Error', which is how a failed storage PUT surfaces.
export const reportKlydoLoopUploadFailure = ({
  error,
  timing,
  fileName,
  fileSizeBytes,
}: {
  error: unknown;
  timing: KlydoLoopUploadTiming;
  fileName: string;
  fileSizeBytes: number;
}) => {
  const step = getKlydoLoopUploadFailedStep(timing, error);
  const message = error instanceof Error ? error.message : String(error);
  Sentry.captureException(
    new Error(`Klydo loop upload failed at ${step}: ${message}`),
    {
      tags: { klydoLoopUploadStep: step },
      extra: {
        errorName: error instanceof Error ? error.name : undefined,
        axiosCode: axios.isAxiosError(error) ? error.code : undefined,
        httpStatus: axios.isAxiosError(error)
          ? error.response?.status
          : undefined,
        fileName,
        fileSizeBytes,
        fileKey: timing.fileKey,
        signedUrlMs: timing.signedUrlMs,
      },
    },
  );
};

const MIN_TRANSPARENT_RATIO = 0.1;

export const checkHasTransparentBackground = (file: File): Promise<boolean> =>
  new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(false);
        ctx.drawImage(img, 0, 0);
        const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const totalPixels = data.length / 4;
        let transparentPixels = 0;
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] === 0) transparentPixels++;
        }
        resolve(transparentPixels / totalPixels >= MIN_TRANSPARENT_RATIO);
      } catch {
        resolve(false);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(false);
    };

    img.src = objectUrl;
  });

// Some browsers never settle a media element for a file they cannot decode, so
// the read is bounded. Callers decide what an unknown duration means for them.
export const AUDIO_DURATION_READ_TIMEOUT_MS = 5000;

// Resolves undefined when the browser cannot report a usable duration: a decode
// error, a non-finite duration (some VBR MP3s report Infinity), or no metadata
// within AUDIO_DURATION_READ_TIMEOUT_MS.
export const readAudioDurationInSeconds = (
  file: File,
): Promise<number | undefined> =>
  new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const audio = new Audio();
    const finish = (duration?: number) => {
      clearTimeout(timeoutId);
      URL.revokeObjectURL(objectUrl);
      resolve(duration);
    };

    const timeoutId = setTimeout(
      () => finish(undefined),
      AUDIO_DURATION_READ_TIMEOUT_MS,
    );

    audio.onloadedmetadata = () =>
      finish(Number.isFinite(audio.duration) ? audio.duration : undefined);
    audio.onerror = () => finish(undefined);
    audio.src = objectUrl;
  });
